import angular from 'angular'
import inspect from 'object-inspect'

import { isText, isAllDefined, hasUndefined } from '../utils/bits'
import { wrap, flow, camelCase, upperFirst, uniqueId } from 'lodash-es'

const capitalCase = flow(camelCase, upperFirst)

import envRaw from '../utils/env-raw'
import deline from '../utils/deline'
import extractRawElm from '../utils/extract-raw-elm'

import { flattenEmitters } from '../utils/sum-emitter'

const dropLongSpaces = (str) => str.replace(/\s{2,}/g, ' ')
const clearClassName = (className) => dropLongSpaces(deline(className))

import {
  nextRegId,
  register,
  release
} from './subscriber-manager.js'

import {
  recalcClass,
  recalcStyle,
  recalcShow,
  recalcHide,
  recalcAttr,
  recalcProp,
  recalcBind,
  recalcIf,
  recalcReset
} from './recalcs.js'

// directive types
const CLASS_TYPE = 'class'
const STYLE_TYPE = 'style'
const SHOW_TYPE  = 'show'
const HIDE_TYPE  = 'hide'
const ATTR_TYPE  = 'attr'
const PROP_TYPE  = 'prop'
const BIND_TYPE  = 'bind'
const IF_TYPE    = 'if'
const RESET_TYPE = 'reset'

// type -> recalc
const TYPE_TO_RECALC = {
  [CLASS_TYPE]: recalcClass,
  [STYLE_TYPE]: recalcStyle,
  [SHOW_TYPE]:  recalcShow,
  [HIDE_TYPE]:  recalcHide,
  [ATTR_TYPE]:  recalcAttr,
  [PROP_TYPE]:  recalcProp,
  [BIND_TYPE]:  recalcBind,
  [IF_TYPE]:    recalcIf,
  [RESET_TYPE]: recalcReset
}

// reg dirs
const MODULE_NAME = 'ggs.recalc-onevent'
const EMITTER_SUFFIX = 'Emitter'
const BASE_DIRECTIVE_NAME = 'roe'

registerDirective(CLASS_TYPE, makeCommonDirective)
registerDirective(STYLE_TYPE, makeCommonDirective)
registerDirective(SHOW_TYPE,  makeCommonDirective)
registerDirective(HIDE_TYPE,  makeCommonDirective)
registerDirective(ATTR_TYPE,  makeCommonDirective)
registerDirective(PROP_TYPE,  makeCommonDirective)
registerDirective(BIND_TYPE,  makeCommonDirective)
registerDirective(IF_TYPE,    makeIfDirective)
registerDirective(RESET_TYPE, makeResetDirective)

function registerDirective(type, makeDirective) {
  const recalc = TYPE_TO_RECALC[type]
  const fullName = BASE_DIRECTIVE_NAME + capitalCase(type)
  const cmdlineAttrName = fullName
  const emitterAttrName = fullName + EMITTER_SUFFIX
  const directiveProvider = makeDirective(recalc, cmdlineAttrName, emitterAttrName)

  return angular
    .module(MODULE_NAME)
    .directive(fullName, directiveProvider)
}

// make waitSign

function makeWaitSign(cmdlineAttrName, $elm) {
  const elm = extractRawElm($elm)
  const elmId = elm.id
  const elmClass = elm.className

  const uSign = `[uid=${uniqueId()}]`
  const iSign = isText(elmId) ? `[elmId=${elmId}]` : ''
  const cSign = isText(elmClass) ? `[elmClass=${clearClassName(elmClass)}]` : ''
  return `${cmdlineAttrName}${uSign}${iSign}${cSign}`
}

// make dir
function makeCommonDirective(
  recalc,
  cmdlineAttrName,
  emitterAttrName) {

  return function RecalcOneventProvider($parse, $log, qh, waitFor, Watchdog) {
    'ngInject'

    // realLink
    function realLink($scope, $elm, $attr) {
      const cmdline = $attr[cmdlineAttrName]
      const emitterList = flattenEmitters($scope.$eval($attr[emitterAttrName]))

      const cmdprsd = $parse(cmdline)
      const cmdfunc = () => cmdprsd($scope)
      const brecalc = () => recalc(reg)
      const reg = {
        id: nextRegId(),
        $elm,
        cmdprsd,
        cmdfunc,
        cmdline,
        brecalc,
        last: undefined
      }

      let waitInst
      const test = cmdfunc()
      if (hasUndefined(test)) {
        const waitSign = makeWaitSign(cmdlineAttrName, $elm)
        $log.debug(`${waitSign}: waitFor:init for [cmdline=${cmdline}] because of [test=${inspect(test)}]`)

        waitInst = waitFor(
          () => isAllDefined(cmdfunc()),
          (it) => it === true)
        waitInst.then(() => {
          reg.brecalc()
          $log.debug(`${waitSign}: waitFor:done for [cmdline=${cmdline}] with [reg.last=${inspect(reg.last)}]`)
        }).catch(qh.catchCanceled(() => {
          $log.debug(`${waitSign}: waitFor:canceled for [cmdline=${cmdline}]`)
        })).catch((err) => {
          $log.error(`${waitSign}: waitFor:fail for [cmdline=${cmdline}]`, err)
        })

        if (envRaw.useRoeStuckDetector) {
          Watchdog(1e3, waitInst, wrap(`${waitSign}: waitFor:stuck for [cmdline=${cmdline}] still returns undefined values!`, $log.warn))
        }
      } else {
        reg.brecalc()
      }

      register(reg, emitterList)
      $scope.$on('$destroy', () => {
        if (waitInst) waitInst.cancel()
        release(reg, emitterList)
      })
    }

    // export
    return {
      restrict: 'A',
      priority: 500,
      link: realLink
    }
  }
}

function makeIfDirective(
  recalc,
  cmdlineAttrName,
  emitterAttrName) {

  return function RecalcOneventProvider($parse, $log, qh, waitFor, Watchdog) {
    'ngInject'

    // realLink
    function realLink($scope, $elm, $attr, _$ctrl, $transclude) {
      const cmdline = $attr[cmdlineAttrName]
      const emitterList = flattenEmitters($scope.$eval($attr[emitterAttrName]))

      const cmdprsd = $parse(cmdline)
      const cmdfunc = () => cmdprsd($scope)
      const brecalc = () => recalc(reg)
      const reg = {
        id: nextRegId(),
        $elm,
        cmdprsd,
        cmdfunc,
        cmdline,
        brecalc,
        $transclude,
        last: undefined,
        $childElm: undefined,
        $childScope: undefined
      }

      let waitInst
      const test = cmdfunc()
      if (hasUndefined(test)) {
        const waitSign = makeWaitSign(cmdlineAttrName, $elm)
        $log.debug(`${waitSign}: waitFor:init for [cmdline=${cmdline}] because of [test=${inspect(test)}]`)

        waitInst = waitFor(
          () => isAllDefined(cmdfunc()),
          (it) => it === true)
        waitInst.then(() => {
          reg.brecalc()
          $log.debug(`${waitSign}: waitFor:done for [cmdline=${cmdline}] with [reg.last=${inspect(reg.last)}]`)
        }).catch(qh.catchCanceled(() => {
          $log.debug(`${waitSign}: waitFor:canceled for [cmdline=${cmdline}]`)
        })).catch((err) => {
          $log.error(`${waitSign}: waitFor:fail for [cmdline=${cmdline}]`, err)
        })

        if (envRaw.useRoeStuckDetector) {
          Watchdog(1e3, waitInst, wrap(`${waitSign}: waitFor:stuck for [cmdline=${cmdline}] still returns undefined values!`, $log.warn))
        }
      } else {
        reg.brecalc()
      }

      register(reg, emitterList)
      $scope.$on('$destroy', () => {
        if (waitInst) waitInst.cancel()
        release(reg, emitterList)
      })
    }

    // export
    return {
      multiElement: true,
      transclude: 'element',
      priority: 600,
      terminal: true,
      restrict: 'A',
      $$tlb: true,
      link: realLink
    }
  }
}

function makeResetDirective(
  recalc,
  _cmdlineAttrName,
  emitterAttrName) {

  return function RecalcOneventProvider() {
    'ngInject'

    // realLink
    function realLink($scope, $elm, $attr, _$ctrl, $transclude) {
      const emitterList = flattenEmitters($scope.$eval($attr[emitterAttrName]))
      const brecalc = () => recalc(reg)
      const reg = {
        id: nextRegId(),
        $elm,
        brecalc,
        $transclude,
        last: undefined,
        $childElm: undefined,
        $childScope: undefined
      }

      reg.brecalc()
      register(reg, emitterList)
      $scope.$on('$destroy', () => {
        release(reg, emitterList)
      })
    }

    // export
    return {
      multiElement: true,
      transclude: 'element',
      priority: 600,
      terminal: true,
      restrict: 'A',
      $$tlb: true,
      link: realLink
    }
  }
}
